import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import { format, parseISO } from 'date-fns';

import {
  Button,
  Form as BootstrapForm,
  Col,
  Container,
  Table as BootstrapTable,
  Badge,
} from 'react-bootstrap';
import { FaSearch, FaPlus, FaFilter, FaEdit } from 'react-icons/fa';

import { useAuth } from '../../hooks/auth';
import { useMedico } from '../../hooks/medico';
import {
  useReceita,
  IReceitaSearch,
  IFiltrosReceita,
} from '../../hooks/receita';
import Loading from '../../components/Loading';
import SelectAsync from '../../components/SelectAsync';
import Input from '../../components/Input';
import StatusBadge from '../../components/Receita/StatusBadge';

import getValidationErrors from '../../utils/getValidationErrors';

import {
  TableContainer,
  TableRow,
  TableCell,
  ActionButtons,
  FilterCard,
  FilterRow,
} from './styles';
import { formataCPF } from '../../utils/funcoes';

const TIPO_USUARIO = {
  ADMINISTRADOR: 1,
  SUPERVISOR: 2,
  ATENDENTE_CAIXA: 3,
  ATENDENTE: 4,
  MEDICO: 6,
};

const Receitas: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth();
  const { optionsMedico, buscaOptionsMedico } = useMedico();
  const { listarReceitas, loading: loadingReceitas } = useReceita();

  const formRef = useRef<FormHandles>(null);
  const [receitas, setReceitas] = useState<IReceitaSearch[]>([]);
  const [loading, setLoading] = useState(false);
  const [paginacao, setPaginacao] = useState<{
    pagina: number;
    limite: number;
    temMais: boolean;
  }>({
    pagina: 1,
    limite: 20,
    temMais: true,
  });
  const [filtros, setFiltros] = useState<IFiltrosReceita>({
    status: '',
    dataInicio: '',
    dataFim: '',
  });
  const [showFiltros, setShowFiltros] = useState(false);

  const isMedico = user.UsuTipo === TIPO_USUARIO.MEDICO;
  const isAdminOuSupervisor = user.UsuTipo <= TIPO_USUARIO.SUPERVISOR;

  useEffect(() => {
    if (isAdminOuSupervisor) {
      buscaOptionsMedico();
    }
  }, [buscaOptionsMedico, isAdminOuSupervisor]);

  const carregarReceitas = useCallback(
    async (pagina = 1, novosFiltros?: IFiltrosReceita) => {
      setLoading(true);
      try {
        const filtrosParaBusca = {
          ...filtros,
          ...novosFiltros,
          pagina,
          limite: paginacao.limite,
        };
        const { data, pagination } = await listarReceitas(filtrosParaBusca);

        const receitaFormatada = data.map(rec => {
          return { ...rec, RecPacCPFFormatado: formataCPF(rec.RecPacCPF) };
        });

        if (pagina === 1) {
          setReceitas(receitaFormatada);
        } else {
          setReceitas(prev => [...prev, ...data]);
        }

        const temMais = paginacao.pagina < pagination.totalPages;
        setPaginacao(prev => ({ ...prev, pagina, temMais }));
      } catch (error) {
        // Error already handled in hook
      } finally {
        setLoading(false);
      }
    },
    [filtros, listarReceitas, paginacao.limite],
  );

  useEffect(() => {
    carregarReceitas(1);
  }, [carregarReceitas]);

  const handleSubmitFiltros = useCallback(
    async (dados: IFiltrosReceita) => {
      try {
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          paciente: Yup.string(),
          medico: Yup.string(),
          status: Yup.string(),
          dataInicio: Yup.string(),
          dataFim: Yup.string(),
        });

        await schema.validate(dados, { abortEarly: false });

        const novosFiltros: IFiltrosReceita = {
          paciente: dados.paciente || '',
          medico: dados.medico || '',
          status: (dados.status || '') as IFiltrosReceita['status'],
          dataInicio: dados.dataInicio || '',
          dataFim: dados.dataFim || '',
        };

        setFiltros(novosFiltros);
        await carregarReceitas(1, novosFiltros);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRef.current?.setErrors(errors);
        }
      }
    },
    [carregarReceitas],
  );

  const handleLimparFiltros = useCallback(() => {
    formRef.current?.setFieldValue('paciente', '');
    formRef.current?.setFieldValue('medico', '');
    formRef.current?.setFieldValue('status', '');
    formRef.current?.setFieldValue('dataInicio', '');
    formRef.current?.setFieldValue('dataFim', '');
    setFiltros({ status: '', dataInicio: '', dataFim: '' });
    carregarReceitas(1, {
      paciente: '',
      medico: '',
      status: '',
      dataInicio: '',
      dataFim: '',
    });
  }, [carregarReceitas]);

  const handleNovaReceita = useCallback(() => {
    history.push('/receita/cadastro');
  }, [history]);

  const handleEditar = useCallback(
    (codigo: number) => {
      history.push(`/receita/editar/${codigo}`);
    },
    [history],
  );

  const handleVer = useCallback(
    (codigo: number) => {
      history.push(`/receita/${codigo}`);
    },
    [history],
  );

  const formatarData = (data: Date | string): string => {
    try {
      return format(parseISO(data.toString()), 'dd/MM/yyyy HH:mm');
    } catch {
      return '-';
    }
  };

  const statusOptions = [
    { label: 'Todos', value: '' },
    { label: 'Criada', value: 'Criada' },
    { label: 'Emitida', value: 'Emitida' },
    { label: 'Cancelada', value: 'Cancelada' },
  ];

  return (
    <Container fluid className="mt-2">
      {(loading || loadingReceitas) && <Loading />}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Receitas</h2>
        {isMedico && (
          <Button variant="success" onClick={handleNovaReceita}>
            <FaPlus size={16} /> Nova Receita
          </Button>
        )}
      </div>

      <FilterCard>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">Filtros</h5>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowFiltros(!showFiltros)}
          >
            <FaFilter size={14} /> {showFiltros ? 'Ocultar' : 'Exibir'} Filtros
          </Button>
        </div>

        {showFiltros && (
          <Form ref={formRef} onSubmit={handleSubmitFiltros}>
            <FilterRow>
              <Col md={4} className="pr-1">
                <Input
                  name="paciente"
                  label="Paciente"
                  placeholder="Nome do paciente"
                  maxLength={100}
                />
              </Col>
              {isAdminOuSupervisor && (
                <Col md={4} className="pr-1">
                  <SelectAsync
                    name="medico"
                    label="Médico"
                    placeholder="Selecione o médico"
                    urlApi="medicos/combo?termoBusca="
                    options={optionsMedico}
                    isClearable
                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                    handleSelectChange={(_e: any) => {}}
                  />
                </Col>
              )}
              <Col md={3} className="pr-1">
                <BootstrapForm.Control
                  as="select"
                  name="status"
                  className="form-control"
                >
                  {statusOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </BootstrapForm.Control>
              </Col>
            </FilterRow>

            <FilterRow>
              <Col md={3} className="pr-1">
                <Input
                  name="dataInicio"
                  label="Data Início"
                  type="date"
                  maxLength={10}
                />
              </Col>
              <Col md={3} className="pr-1">
                <Input
                  name="dataFim"
                  label="Data Fim"
                  type="date"
                  maxLength={10}
                />
              </Col>
              <Col md={2} className="pr-1 d-flex align-items-end">
                <Button type="submit" variant="primary" className="w-100">
                  <FaSearch size={14} /> Buscar
                </Button>
              </Col>
              <Col md={2} className="pr-1 d-flex align-items-end">
                <Button
                  type="button"
                  variant="outline-secondary"
                  className="w-100"
                  onClick={handleLimparFiltros}
                >
                  Limpar
                </Button>
              </Col>
            </FilterRow>
          </Form>
        )}
      </FilterCard>

      <TableContainer>
        <BootstrapTable striped bordered hover size="sm" className="mb-0">
          <thead className="thead-light">
            <tr>
              <th style={{ width: '5%' }}>#</th>
              <th style={{ width: '10%' }}>Tipo</th>
              <th style={{ width: '25%' }}>Paciente</th>
              <th style={{ width: '20%' }}>Médico</th>
              <th style={{ width: '12%' }}>Status</th>
              <th style={{ width: '18%' }}>Data</th>

              <th style={{ width: '10%' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {receitas.length === 0 ? (
              <tr>
                <TableCell colSpan={7} className="text-center py-4 text-muted">
                  Nenhuma receita encontrada
                </TableCell>
              </tr>
            ) : (
              receitas.map(receita => (
                <TableRow key={receita.RecCodigo}>
                  <TableCell>{receita.RecCodigo}</TableCell>
                  <TableCell>
                    <Badge
                      style={{
                        fontSize: '0.9rem',
                        padding: '6px 8px',
                        minWidth: '88px',
                      }}
                      variant={receita.RecControlado ? 'warning' : 'secondary'}
                    >
                      {receita.RecControlado ? 'Controlado' : 'Simples'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <strong>{receita.RecPacNome}</strong>
                    {/* {receita.RecPacCPF && <br />} */} <br />
                    <small className="text-muted">
                      {receita.RecPacCPFFormatado}
                    </small>
                  </TableCell>
                  <TableCell>
                    <strong>{receita.RecUsuNome}</strong>
                    {receita.RecUsuCRM && (
                      <>
                        <br />
                        <small className="text-muted">
                          {receita.RecUsuCRM}
                        </small>
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusBadge size="lg" status={receita.RecStatus} />
                  </TableCell>
                  <TableCell>{formatarData(receita.created_at)}</TableCell>

                  <TableCell>
                    <ActionButtons>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleVer(receita.RecCodigo)}
                        title="Visualizar"
                      >
                        <FaSearch size={14} />
                      </Button>
                      {(isMedico || isAdminOuSupervisor) &&
                        receita.RecStatus === 'Criada' && (
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => handleEditar(receita.RecCodigo)}
                            title="Editar"
                          >
                            <FaEdit size={14} />
                          </Button>
                        )}
                    </ActionButtons>
                  </TableCell>
                </TableRow>
              ))
            )}
          </tbody>
        </BootstrapTable>
      </TableContainer>

      {receitas.length > 0 && paginacao.temMais && (
        <div className="d-block text-center py-3">
          <Button
            type="button"
            variant="outline-secondary"
            className="rounded-0"
            onClick={() => carregarReceitas(paginacao.pagina + 1)}
            disabled={loading}
          >
            {loading ? 'Carregando...' : 'Carregar mais'}
          </Button>
        </div>
      )}
    </Container>
  );
};

export default Receitas;
